import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Filter as FilterIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  adminService,
  clienteService,
  recorrenciaService,
  setorService,
  tipoObrigacaoService,
} from "../services/api";

const Recorrencias = () => {
  const { user } = useAuth();

  // ✅ CONFIGURAÇÃO DOS TAMANHOS DOS FILTROS - CSS PURO RESPONSIVO!
  // Valores de 0 a 100 (porcentagem da largura no desktop)
  // Mobile: sempre 100% (empilhados)
  // 💡 TESTE: Mude os valores e veja a diferença imediatamente!
  const TAMANHOS_FILTROS = {
    cliente: 34, // 34% - Cliente
    tipoObrigacao: 33, // 33% - Tipo de Obrigação
    setor: 33, // 33% - Setor
  };

  const [recorrencias, setRecorrencias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tiposObrigacao, setTiposObrigacao] = useState([]);
  const [setores, setSetores] = useState([]);
  const [escritorios, setEscritorios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  // Estado para controlar visibilidade dos filtros
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const [filtros, setFiltros] = useState({
    cliente_id: "",
    tipo_obrigacao_id: "",
    setor_id: "",
    escritorio_id_filter: "", // Novo filtro para super admin
  });
  const [paginacao, setPaginacao] = useState({
    page: 0,
    limit: 10,
    total: 0,
  });

  // Estados para modal de recorrência
  const [modalAberto, setModalAberto] = useState(false);
  const [recorrenciaEditando, setRecorrenciaEditando] = useState(null);
  const [dadosRecorrencia, setDadosRecorrencia] = useState({
    cliente_id: "",
    tipo_obrigacao_id: "",
    dia_recorrente: "",
    mes_recorrente: "",
    setor_id: "",
  });

  // Estados para notificações
  const [snackbar, setSnackbar] = useState({
    aberto: false,
    mensagem: "",
    tipo: "success",
  });

  // Estados para confirmação de exclusão
  const [confirmacaoExclusao, setConfirmacaoExclusao] = useState({
    aberto: false,
    recorrenciaId: null,
    recorrenciaDescricao: "",
  });

  const carregarRecorrencias = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: paginacao.page + 1,
        limit: paginacao.limit,
      };

      if (filtros.cliente_id) params.cliente_id = filtros.cliente_id;
      if (filtros.tipo_obrigacao_id)
        params.tipo_obrigacao_id = filtros.tipo_obrigacao_id;
      if (filtros.setor_id) params.setor_id = filtros.setor_id;
      if (filtros.escritorio_id_filter)
        params.escritorio_id_filter = filtros.escritorio_id_filter;

      const response = await recorrenciaService.getAll(params);
      setRecorrencias(response.recorrencias);
      setPaginacao((prev) => ({
        ...prev,
        total: response.pagination.total,
      }));
    } catch (error) {
      mostrarSnackbar(
        "Erro ao carregar recorrências: " + error.message,
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [paginacao.page, paginacao.limit, filtros]);

  const carregarClientes = useCallback(async () => {
    try {
      // Filtrando apenas clientes ativos criados pelo usuário logado
      const response = await clienteService.getAll({
        ativo: true,
        criado_por_id: user?.id,
        limit: 1000,
      });
      setClientes(response.clientes || []);
    } catch (error) {
      mostrarSnackbar("Erro ao carregar clientes: " + error.message, "error");
    }
  }, [user?.id]);

  const carregarTiposObrigacao = useCallback(async () => {
    try {
      const response = await tipoObrigacaoService.getAll({ limit: 1000 });
      setTiposObrigacao(response.tipos_obrigacoes || []);
    } catch (error) {
      mostrarSnackbar(
        "Erro ao carregar tipos de obrigação: " + error.message,
        "error",
      );
    }
  }, []);

  const carregarSetores = useCallback(async () => {
    try {
      const response = await setorService.getAll({ limit: 100 });
      setSetores(response.setores || []);
    } catch (error) {
      mostrarSnackbar("Erro ao carregar setores: " + error.message, "error");
    }
  }, []);

  const carregarEscritorios = useCallback(async () => {
    try {
      const response = await adminService.getEscritorios();
      // A API retorna diretamente um array, não um objeto com propriedade escritorios
      setEscritorios(Array.isArray(response) ? response : []);
    } catch (error) {
      mostrarSnackbar(
        "Erro ao carregar escritórios: " + error.message,
        "error",
      );
    }
  }, []);

  useEffect(() => {
    carregarRecorrencias();
  }, [carregarRecorrencias]);

  useEffect(() => {
    // Só carrega clientes se o usuário estiver disponível
    if (user?.id) {
      carregarClientes();
    }
    carregarTiposObrigacao();
    carregarSetores();
    if (user?.is_super_admin) {
      carregarEscritorios();
    }
  }, [
    carregarClientes,
    carregarTiposObrigacao,
    carregarSetores,
    carregarEscritorios,
    user,
  ]);

  const mostrarSnackbar = (mensagem, tipo = "success") => {
    setSnackbar({
      aberto: true,
      mensagem,
      tipo,
    });
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
    setPaginacao((prev) => ({ ...prev, page: 0 }));
  };

  const abrirModalRecorrencia = (recorrencia = null) => {
    if (recorrencia) {
      setRecorrenciaEditando(recorrencia);
      setDadosRecorrencia({
        cliente_id: recorrencia.cliente_id || "",
        tipo_obrigacao_id: recorrencia.tipo_obrigacao_id || "",
        dia_recorrente: recorrencia.dia_recorrente || "",
        mes_recorrente: recorrencia.mes_recorrente || "",
        setor_id: recorrencia.setor_id || "",
      });
    } else {
      setRecorrenciaEditando(null);
      setDadosRecorrencia({
        cliente_id: "",
        tipo_obrigacao_id: "",
        dia_recorrente: "",
        mes_recorrente: "",
        setor_id: "",
      });
    }
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setRecorrenciaEditando(null);
  };

  const handleInputChange = (campo, valor) => {
    setDadosRecorrencia((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleTipoObrigacaoChange = (tipoObrigacaoId) => {
    // Encontrar o tipo de obrigação selecionado
    const tipoObrigacao = tiposObrigacao.find(
      (tipo) => tipo.id === parseInt(tipoObrigacaoId),
    );

    if (tipoObrigacao) {
      // Preencher automaticamente os campos de dia e mês recorrente e setor
      setDadosRecorrencia((prev) => ({
        ...prev,
        tipo_obrigacao_id: tipoObrigacaoId,
        dia_recorrente: tipoObrigacao.dia_recorrente?.toString() || "",
        mes_recorrente: tipoObrigacao.mes_recorrente?.toString() || "",
        setor_id: tipoObrigacao.setor?.id?.toString() || "",
      }));
    } else {
      // Se nenhum tipo for selecionado, limpar os campos
      setDadosRecorrencia((prev) => ({
        ...prev,
        tipo_obrigacao_id: tipoObrigacaoId,
        dia_recorrente: "",
        mes_recorrente: "",
        setor_id: "",
      }));
    }
  };

  const salvarRecorrencia = async () => {
    if (salvando) return; // Evita cliques duplos

    try {
      setSalvando(true);

      if (!dadosRecorrencia.cliente_id || !dadosRecorrencia.tipo_obrigacao_id) {
        mostrarSnackbar(
          "Cliente e tipo de obrigação são obrigatórios",
          "error",
        );
        return;
      }

      if (!dadosRecorrencia.dia_recorrente) {
        mostrarSnackbar("Dia recorrente é obrigatório", "error");
        return;
      }

      const dados = {
        cliente_id: dadosRecorrencia.cliente_id,
        tipo_obrigacao_id: dadosRecorrencia.tipo_obrigacao_id,
        dia_recorrente: parseInt(dadosRecorrencia.dia_recorrente),
        mes_recorrente: dadosRecorrencia.mes_recorrente
          ? parseInt(dadosRecorrencia.mes_recorrente)
          : null,
        setor_id: dadosRecorrencia.setor_id || null,
      };

      if (recorrenciaEditando) {
        await recorrenciaService.update(recorrenciaEditando.id, dados);
        mostrarSnackbar("Recorrência atualizada com sucesso!");
      } else {
        const response = await recorrenciaService.create(dados);
        if (response.obrigacao_foi_criada) {
          // Formatar data sem problemas de timezone
          const dataVencimento = response.obrigacao_criada.data_vencimento
            .split("T")[0]
            .split("-")
            .reverse()
            .join("/");
          mostrarSnackbar(
            `Recorrência criada com sucesso! Primeira obrigação criada automaticamente com vencimento em ${dataVencimento}`,
          );
        } else {
          mostrarSnackbar(
            "Recorrência criada com sucesso! (primeira obrigação já existia para esta data)",
          );
        }
      }

      fecharModal();
      carregarRecorrencias();
    } catch (error) {
      mostrarSnackbar("Erro ao salvar recorrência: " + error.message, "error");
    } finally {
      setSalvando(false);
    }
  };

  const confirmarExclusao = (recorrencia) => {
    const clienteNome = recorrencia.Cliente?.nome || "Cliente desconhecido";
    const tipoObrigacao =
      recorrencia.TipoObrigacao?.descricao || "Tipo desconhecido";

    setConfirmacaoExclusao({
      aberto: true,
      recorrenciaId: recorrencia.id,
      recorrenciaDescricao: `${clienteNome} - ${tipoObrigacao}`,
    });
  };

  const excluirRecorrencia = async () => {
    if (excluindo) return; // Evita cliques duplos

    try {
      setExcluindo(true);
      await recorrenciaService.delete(confirmacaoExclusao.recorrenciaId);
      mostrarSnackbar("Recorrência excluída com sucesso!");
      setConfirmacaoExclusao({
        aberto: false,
        recorrenciaId: null,
        recorrenciaDescricao: "",
      });
      carregarRecorrencias();
    } catch (error) {
      mostrarSnackbar("Erro ao excluir recorrência: " + error.message, "error");
    } finally {
      setExcluindo(false);
    }
  };

  const formatarRecorrencia = (recorrencia) => {
    if (recorrencia.dia_recorrente && recorrencia.mes_recorrente) {
      return `Dia ${recorrencia.dia_recorrente} do mês ${recorrencia.mes_recorrente}`;
    } else if (recorrencia.dia_recorrente) {
      return `Todo dia ${recorrencia.dia_recorrente}`;
    } else if (recorrencia.mes_recorrente) {
      return `Todo mês ${recorrencia.mes_recorrente}`;
    }
    return "Não definido";
  };

  const getNomeCliente = (cliente) => {
    if (!cliente) return "Cliente não encontrado";
    return cliente.nome || cliente.fantasia;
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600, color: "#333" }}>
          Recorrências
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            sx={{ mr: 2 }}
          >
            Filtros
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => abrirModalRecorrencia()}
            sx={{ backgroundColor: "#1976d2" }}
          >
            Nova recorrência
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      {mostrarFiltros && (
        <Card
          sx={{
            mb: 3,
            p: 3,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, color: "#333", fontWeight: 600 }}
          >
            Filtros
          </Typography>

          {/* ✅ SOLUÇÃO CSS PURA - Funciona em qualquer versão do Material-UI */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              flexWrap: { xs: "wrap", md: "nowrap" }, // Mobile: quebra linha, Desktop: mesma linha
              alignItems: "center",
            }}
          >
            {/* Cliente */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.cliente}%`,
                minWidth: { xs: "100%", md: "250px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.cliente}%` },
              }}
            >
              <FormControl fullWidth size="medium">
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={filtros.cliente_id}
                  label="Cliente"
                  onChange={(e) =>
                    handleFiltroChange("cliente_id", e.target.value)
                  }
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <MenuItem value="">Todos os clientes</MenuItem>
                  {(clientes || []).map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {getNomeCliente(cliente)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Tipo de Obrigação */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.tipoObrigacao}%`,
                minWidth: { xs: "100%", md: "250px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.tipoObrigacao}%` },
              }}
            >
              <FormControl fullWidth size="medium">
                <InputLabel>Tipo de obrigação</InputLabel>
                <Select
                  value={filtros.tipo_obrigacao_id}
                  label="Tipo de obrigação"
                  onChange={(e) =>
                    handleFiltroChange("tipo_obrigacao_id", e.target.value)
                  }
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <MenuItem value="">Todos os tipos</MenuItem>
                  {(tiposObrigacao || []).map((tipo) => (
                    <MenuItem key={tipo.id} value={tipo.id}>
                      {tipo.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Setor */}
            <Box
              sx={{
                flex: `0 1 ${TAMANHOS_FILTROS.setor}%`,
                minWidth: { xs: "100%", md: "250px" },
                maxWidth: { md: `${TAMANHOS_FILTROS.setor}%` },
              }}
            >
              <FormControl fullWidth size="medium">
                <InputLabel>Setor</InputLabel>
                <Select
                  value={filtros.setor_id}
                  label="Setor"
                  onChange={(e) =>
                    handleFiltroChange("setor_id", e.target.value)
                  }
                  sx={{
                    backgroundColor: "white",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  <MenuItem value="">Todos os setores</MenuItem>
                  <MenuItem value="sem_setor">Sem setor</MenuItem>
                  {(setores || []).map((setor) => (
                    <MenuItem key={setor.id} value={setor.id}>
                      {setor.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Filtro de Escritório - apenas para super admin */}
            {user?.is_super_admin && (
              <Box
                sx={{
                  flex: `0 1 ${TAMANHOS_FILTROS.cliente}%`,
                  minWidth: { xs: "100%", md: "250px" },
                  maxWidth: { md: `${TAMANHOS_FILTROS.cliente}%` },
                }}
              >
                <FormControl fullWidth size="medium">
                  <InputLabel>Escritório</InputLabel>
                  <Select
                    value={filtros.escritorio_id_filter}
                    label="Escritório"
                    onChange={(e) =>
                      handleFiltroChange("escritorio_id_filter", e.target.value)
                    }
                    sx={{
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                      },
                    }}
                  >
                    <MenuItem value="">Todos os escritórios</MenuItem>
                    {escritorios.map((escritorio) => (
                      <MenuItem key={escritorio.id} value={escritorio.id}>
                        {escritorio.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Box>

          {/* Botão para limpar filtros */}
          <Box sx={{ mt: 3 }}>
            <Button
              onClick={() => {
                setFiltros({
                  cliente_id: "",
                  tipo_obrigacao_id: "",
                  setor_id: "",
                  escritorio_id_filter: "",
                });
                setPaginacao((prev) => ({ ...prev, page: 0 }));
              }}
              variant="outlined"
              size="small"
            >
              Limpar filtros
            </Button>
          </Box>
        </Card>
      )}

      {/* Tabela de recorrências */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  Tipo de obrigação
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Recorrência</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Setor</TableCell>
                {user?.is_super_admin && (
                  <TableCell sx={{ fontWeight: 600 }}>Escritório</TableCell>
                )}
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={user?.is_super_admin ? 7 : 6}
                    align="center"
                  >
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : (recorrencias || []).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={user?.is_super_admin ? 7 : 6}
                    align="center"
                  >
                    Nenhuma recorrência encontrada
                  </TableCell>
                </TableRow>
              ) : (
                (recorrencias || []).map((recorrencia) => (
                  <TableRow key={recorrencia.id}>
                    <TableCell>{getNomeCliente(recorrencia.cliente)}</TableCell>
                    <TableCell>
                      {recorrencia.tipo_obrigacao?.descricao || "N/A"}
                    </TableCell>
                    <TableCell>{formatarRecorrencia(recorrencia)}</TableCell>
                    <TableCell>{recorrencia.setor?.descricao || "-"}</TableCell>
                    {user?.is_super_admin && (
                      <TableCell>
                        {recorrencia.cliente?.escritorio?.nome || "-"}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => abrirModalRecorrencia(recorrencia)}
                          color="info"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => confirmarExclusao(recorrencia)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={paginacao.total}
            page={paginacao.page}
            onPageChange={(event, newPage) =>
              setPaginacao((prev) => ({ ...prev, page: newPage }))
            }
            rowsPerPage={paginacao.limit}
            onRowsPerPageChange={(event) => {
              setPaginacao((prev) => ({
                ...prev,
                limit: parseInt(event.target.value, 10),
                page: 0,
              }));
            }}
            labelRowsPerPage="Itens por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </TableContainer>
      </Card>

      {/* Modal de recorrência */}
      <Dialog open={modalAberto} onClose={fecharModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {recorrenciaEditando ? "Editar recorrência" : "Nova recorrência"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Cliente ocupando a linha inteira */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Cliente *</InputLabel>
                <Select
                  value={dadosRecorrencia.cliente_id}
                  label="Cliente *"
                  onChange={(e) =>
                    handleInputChange("cliente_id", e.target.value)
                  }
                >
                  <MenuItem value="">Selecione um cliente</MenuItem>
                  {(clientes || []).map((cliente) => (
                    <MenuItem key={cliente.id} value={cliente.id}>
                      {getNomeCliente(cliente)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Segunda linha com 3 campos usando flexbox */}
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de obrigação *</InputLabel>
                  <Select
                    value={dadosRecorrencia.tipo_obrigacao_id}
                    label="Tipo de obrigação *"
                    onChange={(e) => handleTipoObrigacaoChange(e.target.value)}
                  >
                    <MenuItem value="">Selecione um tipo</MenuItem>
                    {(tiposObrigacao || []).map((tipo) => (
                      <MenuItem key={tipo.id} value={tipo.id}>
                        {tipo.descricao}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Dia recorrente (1-31) *"
                  type="number"
                  value={dadosRecorrencia.dia_recorrente}
                  onChange={(e) =>
                    handleInputChange("dia_recorrente", e.target.value)
                  }
                  inputProps={{ min: 1, max: 31 }}
                  helperText="Dia do mês para gerar"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  label="Mês recorrente (1-12)"
                  type="number"
                  value={dadosRecorrencia.mes_recorrente}
                  onChange={(e) =>
                    handleInputChange("mes_recorrente", e.target.value)
                  }
                  inputProps={{ min: 1, max: 12 }}
                  helperText="Mês do ano (nas obrigações anuais)"
                />
              </Box>
            </Box>

            {/* Terceira linha com campo de setor */}
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={dadosRecorrencia.setor_id}
                  label="Setor"
                  onChange={(e) =>
                    handleInputChange("setor_id", e.target.value)
                  }
                >
                  <MenuItem value="">Sem setor</MenuItem>
                  {(setores || []).map((setor) => (
                    <MenuItem key={setor.id} value={setor.id}>
                      {setor.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Observações */}
            <Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Observações:</strong>
                <br />
                • Os campos de dia e mês recorrente são preenchidos
                automaticamente baseado no tipo de obrigação selecionado
                <br />
                • Se definir apenas o dia, a obrigação será gerada mensalmente
                neste dia
                <br />• Se definir ambos, a obrigação será gerada anualmente no
                dia e mês específicos
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharModal}>Cancelar</Button>
          <Button
            onClick={salvarRecorrencia}
            variant="contained"
            disabled={salvando}
          >
            {salvando ? (
              <CircularProgress size={20} />
            ) : recorrenciaEditando ? (
              "Atualizar"
            ) : (
              "Criar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={confirmacaoExclusao.aberto}
        onClose={() =>
          setConfirmacaoExclusao({
            aberto: false,
            recorrenciaId: null,
            recorrenciaDescricao: "",
          })
        }
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a recorrência "
            {confirmacaoExclusao.recorrenciaDescricao}"? Esta ação não pode ser
            desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmacaoExclusao({
                aberto: false,
                recorrenciaId: null,
                recorrenciaDescricao: "",
              })
            }
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={excluirRecorrencia}
            color="error"
            variant="contained"
            disabled={excluindo}
          >
            {excluindo ? <CircularProgress size={20} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.aberto}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, aberto: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, aberto: false })}
          severity={snackbar.tipo}
          sx={{ width: "100%" }}
        >
          {snackbar.mensagem}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Recorrencias;
